
import React from "react";
import { 
  Bold, 
  Italic, 
  Link, 
  List, 
  ListOrdered, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  Type,
  Palette
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface EditorToolbarProps {
  onCommand: (command: string, value: string | null) => void;
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({ onCommand }) => {
  // Font sizes for the dropdown
  const fontSizes = [
    { label: "Small", value: "1" },
    { label: "Normal", value: "3" },
    { label: "Medium", value: "4" },
    { label: "Large", value: "5" },
    { label: "Extra Large", value: "6" },
    { label: "Huge", value: "7" },
  ];

  // Colors for the dropdown
  const colors = [
    { label: "Black", value: "#000000" },
    { label: "Gray", value: "#6b7280" },
    { label: "Red", value: "#ef4444" },
    { label: "Orange", value: "#f97316" },
    { label: "Yellow", value: "#eab308" },
    { label: "Green", value: "#22c55e" },
    { label: "Blue", value: "#3b82f6" },
    { label: "Purple", value: "#a855f7" },
    { label: "Pink", value: "#ec4899" },
  ];

  return (
    <div className="bg-muted p-2 flex flex-wrap gap-1 border-b">
      <ToolbarButton icon={<Bold className="h-4 w-4" />} onClick={() => onCommand("bold", null)} />
      <ToolbarButton icon={<Italic className="h-4 w-4" />} onClick={() => onCommand("italic", null)} />
      <ToolbarButton 
        icon={<Link className="h-4 w-4" />} 
        onClick={() => {
          const url = prompt("Enter URL:", "https://");
          if (url) onCommand("createLink", url);
        }} 
      />
      <ToolbarButton icon={<List className="h-4 w-4" />} onClick={() => onCommand("insertUnorderedList", null)} />
      <ToolbarButton icon={<ListOrdered className="h-4 w-4" />} onClick={() => onCommand("insertOrderedList", null)} />
      <ToolbarButton icon={<AlignLeft className="h-4 w-4" />} onClick={() => onCommand("justifyLeft", null)} />
      <ToolbarButton icon={<AlignCenter className="h-4 w-4" />} onClick={() => onCommand("justifyCenter", null)} />
      <ToolbarButton icon={<AlignRight className="h-4 w-4" />} onClick={() => onCommand("justifyRight", null)} />

      {/* Font Size Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm"
            className="h-8 px-2"
          >
            <Type className="h-4 w-4 mr-1" />
            <span className="text-xs">Size</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {fontSizes.map((size) => (
            <DropdownMenuItem 
              key={size.value}
              onClick={() => onCommand("fontSize", size.value)}
            >
              <span style={{ fontSize: `${parseInt(size.value) * 0.25}rem` }}>
                {size.label}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Font Color Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm"
            className="h-8 px-2"
          >
            <Palette className="h-4 w-4 mr-1" />
            <span className="text-xs">Color</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {colors.map((color) => (
            <DropdownMenuItem 
              key={color.value}
              onClick={() => onCommand("foreColor", color.value)}
            >
              <div className="flex items-center">
                <div 
                  className="w-4 h-4 mr-2 rounded-full" 
                  style={{ backgroundColor: color.value }}
                />
                <span>{color.label}</span>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Helper component for toolbar buttons
const ToolbarButton: React.FC<{ 
  icon: React.ReactNode; 
  onClick: () => void;
}> = ({ icon, onClick }) => {
  return (
    <Button 
      type="button" 
      variant="ghost" 
      size="sm" 
      onClick={onClick}
      className="h-8 w-8 p-0"
    >
      {icon}
    </Button>
  );
};
